import { isLocale, type AnalyticsEventInput, type Locale } from '@chess-uno/shared-protocol'
import { SERVER_URL } from './session.js'

/**
 * Tells the server where this visit comes from (referrer, campaign tags), so the
 * publisher can compare Instagram, Google and the rest. No cookie and no
 * persistent id: the visit id is random and lives as long as the tab. Skipped
 * when the browser asks not to be tracked.
 */

const KEY = 'chess-uno:visit'

type Landing = Omit<AnalyticsEventInput, 'type'>

function randomId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
}

function tag(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name)?.trim()
  return value ? value.slice(0, 120) : undefined
}

/** What the browser knows about how this page was reached. */
export function readLanding(location: Pick<Location, 'pathname' | 'search'>, referrer: string, width: number, locale?: Locale): Omit<Landing, 'visitId'> {
  const params = new URLSearchParams(location.search)
  const clickId = (['fbclid', 'igshid', 'gclid'] as const).find((name) => params.has(name))
  return {
    referrer: referrer || undefined,
    landing: location.pathname,
    utmSource: tag(params, 'utm_source') ?? tag(params, 'ref'),
    utmMedium: tag(params, 'utm_medium'),
    utmCampaign: tag(params, 'utm_campaign'),
    clickId,
    locale,
    viewport: width < 760 ? 'mobile' : 'desktop',
  }
}

function send(event: AnalyticsEventInput): void {
  try {
    void fetch(`${SERVER_URL}/api/analytics`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Measuring never gets in the way of the page.
  }
}

function untracked(): boolean {
  return navigator.doNotTrack === '1' || (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true
}

/** Language of the landing page: its prefix (/en, /es…), French at the root. */
function pathLocale(pathname: string): Locale {
  const prefix = pathname.split('/')[1]
  return isLocale(prefix) ? prefix : 'fr'
}

/** The visit of this tab, sent once: later page loads of the tab reuse the stored one. */
export function trackVisit(): void {
  if (typeof window === 'undefined' || untracked()) return
  try {
    if (sessionStorage.getItem(KEY)) return
    const landing: Landing = {
      visitId: randomId(),
      ...readLanding(window.location, document.referrer, window.innerWidth, pathLocale(window.location.pathname)),
    }
    sessionStorage.setItem(KEY, JSON.stringify(landing))
    send({ type: 'visit', ...landing })
  } catch {
    // Storage blocked: without it a visit cannot be told from a reload, so nothing is sent.
  }
}

/** An account was created by the visitor of this tab: credits the source that brought them. */
export function trackSignup(): void {
  if (typeof window === 'undefined' || untracked()) return
  try {
    const raw = sessionStorage.getItem(KEY)
    if (raw) send({ type: 'signup', ...(JSON.parse(raw) as Landing) })
  } catch {
    // Nothing stored: the signup stays unattributed.
  }
}
